import { belopFraDesimaltekst, belopTilTall, type Belop } from '@es/shared';

/**
 * Norwegian number formatting for the screen.
 *
 * Everything here is PRESENTATION and is allowed to be lossy — `belopTilTall`
 * goes through a double, and these amounts are rounded to whole units for
 * display. Nothing formatted here may ever be fed back into a calculation or
 * sent to the API; the exact value is the `Belop` it came from.
 */

const heleTall = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 });

/** Explicit sign, because an avvik's direction is the whole point of the number. */
const medFortegn = new Intl.NumberFormat('nb-NO', {
  maximumFractionDigits: 0,
  signDisplay: 'exceptZero',
});

export function tilBelop(desimaltekst: string): Belop {
  return belopFraDesimaltekst(desimaltekst);
}

export function formaterBelop(desimaltekst: string): string {
  return heleTall.format(belopTilTall(belopFraDesimaltekst(desimaltekst)));
}

/**
 * Like `formaterBelop`, but keeps decimals when the amount has them.
 *
 * Used for the editable forecast cells: rounding 150,5 to "151" in a field the
 * user can edit would misrepresent the value they are about to change.
 */
const finereTall = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 3 });

export function formaterBelopFint(desimaltekst: string): string {
  return finereTall.format(belopTilTall(belopFraDesimaltekst(desimaltekst)));
}

export function formaterAvvik(desimaltekst: string): string {
  return medFortegn.format(belopTilTall(belopFraDesimaltekst(desimaltekst)));
}

const finereMedFortegn = new Intl.NumberFormat('nb-NO', {
  maximumFractionDigits: 3,
  signDisplay: 'exceptZero',
});

export function formaterAvvikFint(desimaltekst: string): string {
  return finereMedFortegn.format(belopTilTall(belopFraDesimaltekst(desimaltekst)));
}

export function formaterProsent(prosent: number | null): string {
  // An em dash, not "0 %" — there is no meaningful percentage deviation from a
  // zero budget, and printing a number would invite someone to act on it.
  if (prosent === null) return '—';
  return `${medFortegn.format(Math.round(prosent * 10) / 10)} %`;
}

/**
 * A percentage that is a LEVEL rather than a deviation.
 *
 * Separate from `formaterProsent` because that one prints a sign: a deviation of
 * five percent is «+5 %». A consumption rate of sixty-two percent is not «+62 %»
 * — nothing was added to anything, and the sign says a change happened that did
 * not.
 */
export function formaterAndelProsent(prosent: number | null): string {
  if (prosent === null) return '—';
  return `${heleTall.format(Math.round(prosent * 10) / 10)} %`;
}

export function enhetstekst(valutaEnhet: 'kr' | 'kr_1000'): string {
  return valutaEnhet === 'kr_1000' ? 'Beløp i tusen kroner' : 'Beløp i kroner';
}

const MANEDSNAVN = [
  'januar',
  'februar',
  'mars',
  'april',
  'mai',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'desember',
] as const;

export function manedsnavn(maned: number): string {
  return MANEDSNAVN[maned - 1] ?? String(maned);
}

/**
 * Whether an avvik is bad news.
 *
 * Uniform across all three kategorier, with no special case for inntekter:
 * income is booked negative, so a shortfall comes out positive just like a cost
 * overrun does. See `beregnAvvik` in @es/shared — positive is always worse for
 * the bottom line.
 */
export type Avviksretning = 'gunstig' | 'noytral' | 'ugunstig';

/**
 * Which way a deviation points — three answers, not two.
 *
 * Zero is its own answer. It used to fall through to «gunstig» and got painted
 * green, which told a reader that landing exactly on budget was a win. It is
 * neither a win nor a problem, and grey says so.
 *
 * RETNING, ikke fortegn: a positive avvik means spending above budget, and that
 * is unfavourable however cheerful the plus sign looks.
 */
export function avviksretning(desimaltekst: string): Avviksretning {
  const belop = belopFraDesimaltekst(desimaltekst);
  if (belop === 0n) return 'noytral';
  return belop > 0n ? 'ugunstig' : 'gunstig';
}

/** Tailwind text colour for a deviation. One place, so the three states cannot drift. */
export function avviksfarge(desimaltekst: string): string {
  const retning = avviksretning(desimaltekst);
  return retning === 'ugunstig'
    ? 'text-negativ'
    : retning === 'gunstig'
      ? 'text-positiv'
      : 'text-noytral';
}

export function erUgunstig(desimaltekst: string): boolean {
  return avviksretning(desimaltekst) === 'ugunstig';
}
